import React, { useState, useEffect } from "react";
import PageLayout from "../components/PageLayout";
import { Link, useParams } from "react-router-dom";
import { FaRegBuilding, FaExternalLinkAlt } from "react-icons/fa";
import { RiMedicineBottleLine, RiLeafLine } from "react-icons/ri";
import ManufacturerAPI from "../api/manufacturer";
import SponsersAffiliations from "../components/SponsersAffiliations";
import CommonBadge from "../components/commons/CommonBadge";
import TopManufactorerBadge from "../components/commons/TopManufactorerBadge";
import TopBrandBadge from "../components/commons/TopBrandBadge";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';

export default function ManufacturerItem() {
    const { uri } = useParams();
    const [manufacturer, setManufacturer] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Add schema microdata for manufacturer page
    useSchemaMicrodata('manufacturer', manufacturer, [manufacturer]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const data = await ManufacturerAPI.getTradeNamesForManufacturer(uri);
                setManufacturer(data);
            } catch (err) {
                setError('Failed to load manufacturer details. Please try again later.');
                console.error('Error fetching manufacturer:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [uri]);

    if (isLoading) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Loading..."
                subtitle="Please wait while we fetch the manufacturer details"
                pageTitle="Loading"
                showSearch={false}
                showGradient={true}
            >
                <div className="flex items-center justify-center h-64">
                    <div className="w-12 h-12 border-t-2 border-b-2 rounded-full animate-spin border-primary"></div>
                </div>
            </PageLayout>
        );
    }

    if (error || !manufacturer) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Error"
                subtitle="Failed to load manufacturer details"
                pageTitle="Error"
                showSearch={false}
                showGradient={true}
            >
                <div className="p-6 text-center border shadow-sm bg-white/20 backdrop-blur-sm border-white/40 rounded-xl">
                    <div className="mb-4 text-red-500">{error || 'Manufacturer not found'}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 text-sm font-medium text-white rounded-md bg-primary hover:bg-primary/90"
                    >
                        Try Again
                    </button>
                </div>

            </PageLayout>
        );
    }

    return (
        <PageLayout
            title=" "
            titleHighlight={manufacturer.manufacturer.name}
            subtitle="Pharmaceutical products and active ingredients"
            pageTitle={manufacturer.name}
            showSearch={false}
            showGradient={true}
            showLogo={true}
            logo={manufacturer.manufacturer.logo_url}
        >
            {/* Manufacturer info */}
            <div className="flex flex-wrap items-center gap-3 mb-8 max-md:justify-center">
                {(manufacturer.is_sponsor || manufacturer.is_top_manufacturer) && (
                    <TopManufactorerBadge />
                )}
                
                <CommonBadge text="Manufacturer" icon={<FaRegBuilding className="size-3.5" />} />
                <CommonBadge text={`${manufacturer.pagination.totalItems} Products`} icon={<RiMedicineBottleLine className="size-3.5" />} />

                {/* External link if available */}
                {manufacturer.website && (
                    <Link to={`${manufacturer.website}?utm_source=pharmacywiki&utm_medium=manufacturer&utm_campaign=visit_website`} target="_blank" >
                        <CommonBadge text={manufacturer.website} icon={<FaExternalLinkAlt className="size-3" />} />
                    </Link>
                )}
                
            </div>

            {/* Products grid */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {manufacturer.tradenames.map((product, index) => (
                    <div
                        key={product.id}
                        className="overflow-hidden transition-all duration-200 border border-white shadow-lg bg-white/50 rounded-xl shadow-light-gray/5 hover:shadow-xl hover:shadow-light-gray/10"
                    >
                        {/* Product header */}
                        <div className="p-4 pb-0 border-b border-secondary/5">
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center justify-center transition-colors duration-300 rounded-full size-7 bg-primary/10 text-primary">
                                        <RiMedicineBottleLine className="size-3.5" />
                                    </div>
                                    <Link
                                        to={`/tradenames/${product.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        className="font-medium transition-colors duration-300 text-14 text-secondary/90 hover:text-primary"
                                    >
                                        {product.name}
                                    </Link>
                                </div>
                            </div>

                            {/* Sub details */}
                            <div className="flex justify-between items-center mb-3">
                                {/* Ingredient count */}
                            
                                <div className="flex items-center gap-1.5 text-14 text-secondary/80">
                                    <RiLeafLine className="size-3.5" />
                                    <span className="font-text">
                                        {product.ingredients.length} active {product.ingredients.length === 1 ? 'ingredient' : 'ingredients'}
                                    </span>
                                </div>
                                
                                {
                                    product.is_sponsor && (
                                        <TopBrandBadge />
                                    )
                                }

                            </div>
                            
                        </div>

                        {/* Ingredients */}
                        <div className="p-4 pt-3 flex flex-wrap gap-1.5">
                            {product.ingredients.map((ingredient, idx) => (
                                <Link
                                    key={ingredient.uri}
                                    to={`/ingredients/${ingredient.uri}`}
                                    className="transition-all duration-200 cursor-pointer bg-secondary/5 text-secondary/80 hover:bg-emerald-600/5 hover:border-emerald-600/20 hover:text-emerald-600 text-14 font-text rounded-full px-2 py-0.5"
                                >
                                    {ingredient.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <SponsersAffiliations hidePadding={true} />
        </PageLayout>
    );
}
